import { auth } from '../firebase';
import { queryCache } from './queryCache';

const COLLEGE_LIST_TTL   = 5 * 60 * 1000;  // 5 min
const COLLEGE_DETAIL_TTL = 10 * 60 * 1000; // 10 min
const COLLEGE_SEARCH_TTL = 2 * 60 * 1000;  // 2 min
const COLLEGE_FILTER_TTL = 10 * 60 * 1000; // 10 min
const COLLEGE_PREVIEW_TTL = 5 * 60 * 1000; // 5 min

class CollegeService {
  static API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  static async getAuthHeaders() {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const token = await currentUser.getIdToken();
        localStorage.setItem('firebaseToken', token);
        return { 'Authorization': `Bearer ${token}` };
      }
    } catch (error) {
      console.warn('Failed to read Firebase auth token:', error);
    }

    const firebaseToken = localStorage.getItem('firebaseToken');
    if (firebaseToken) {
      return { 'Authorization': `Bearer ${firebaseToken}` };
    }

    const adminToken = sessionStorage.getItem('adminToken');
    return adminToken ? { 'Authorization': `Bearer ${adminToken}` } : {};
  }

  static async getColleges(filters = {}) {
    const cacheKey = 'colleges:all';
    const cached = queryCache.get(cacheKey);
    if (cached) return cached;
    try {
      const response = await fetch(`${this.API_BASE_URL}/api/colleges`, {
        headers: { 'Content-Type': 'application/json', ...(await this.getAuthHeaders()) }
      });
      if (response.ok) {
        const data = await response.json();
        const result = data.data || data;
        queryCache.set(cacheKey, result, COLLEGE_LIST_TTL);
        return result;
      }
      throw new Error(`API Error: ${response.status}`);
    } catch (error) {
      console.error('Failed to load colleges from API:', error);
      throw error;
    }
  }

  static async getCollegeById(id) {
    const cacheKey = `college:id:${id}`;
    const cached = queryCache.get(cacheKey);
    if (cached) return cached;
    try {
      const response = await fetch(`${this.API_BASE_URL}/api/colleges/${id}`, {
        headers: { 'Content-Type': 'application/json', ...(await this.getAuthHeaders()) }
      });
      if (response.ok) {
        const data = await response.json();
        queryCache.set(cacheKey, data, COLLEGE_DETAIL_TTL);
        return data;
      }
    } catch (error) {
      console.warn('MongoDB API not available for college details');
    }
    const colleges = await this.getColleges();
    const college = colleges.find(c => c._id === id || c.college_name === id);
    if (college) queryCache.set(cacheKey, college, COLLEGE_DETAIL_TTL);
    return college;
  }

  static async searchColleges({ query = '', state = '', city = '', limit = 10 } = {}) {
    const cacheKey = `college:search:${JSON.stringify({ query: query.trim(), state, city, limit })}`;
    const cached = queryCache.get(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams({ q: query.trim(), limit: String(limit) });
    if (state) params.set('state', state);
    if (city) params.set('city', city);

    const response = await fetch(`${this.API_BASE_URL}/api/colleges/search?${params.toString()}`, {
      headers: { 'Content-Type': 'application/json', ...(await this.getAuthHeaders()) }
    });
    if (!response.ok) throw new Error(`College search API Error: ${response.status}`);

    const data = await response.json();
    const result = { data: data.data || [], total: data.total || 0 };
    queryCache.set(cacheKey, result, COLLEGE_SEARCH_TTL);
    return result;
  }

  static async getCollegeFilters({ state = '' } = {}) {
    const cacheKey = `college:filters:${state || '__all__'}`;
    const cached = queryCache.get(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (state) params.set('state', state);
    const qs = params.toString();

    const response = await fetch(`${this.API_BASE_URL}/api/colleges/filters${qs ? `?${qs}` : ''}`, {
      headers: { 'Content-Type': 'application/json', ...(await this.getAuthHeaders()) }
    });
    if (!response.ok) throw new Error(`College filters API Error: ${response.status}`);

    const data = await response.json();
    queryCache.set(cacheKey, data, COLLEGE_FILTER_TTL);
    return data;
  }

  static async getCollegeListings({ page = 1, limit = 12, search = '' } = {}) {
    const cacheKey = `college:listings:${page}:${limit}:${search.trim()}`;
    const cached = queryCache.get(cacheKey);
    if (cached) return cached;
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit), view: 'list', fields: 'minimal' });
      if (search && search.trim()) params.set('search', search.trim());

      const response = await fetch(`${this.API_BASE_URL}/api/colleges?${params.toString()}`, {
        headers: { 'Content-Type': 'application/json', ...(await this.getAuthHeaders()) }
      });
      if (!response.ok) throw new Error(`College listing API Error: ${response.status}`);

      const data = await response.json();
      const result = {
        data: data.data || [],
        total: data.total || 0,
        page: data.page || page,
        limit: data.limit || limit,
        hasMore: Boolean(data.hasMore)
      };
      queryCache.set(cacheKey, result, COLLEGE_LIST_TTL);
      return result;
    } catch (error) {
      console.error('Failed to load college listings:', error);
      throw error;
    }
  }

  static async getPublicCollegePreview({ limit = 8 } = {}) {
    const safeLimit = Math.min(Math.max(parseInt(limit, 10) || 8, 1), 12);
    const cacheKey = `college:public-preview:${safeLimit}`;
    const cached = queryCache.get(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams({ limit: String(safeLimit) });
    const response = await fetch(`${this.API_BASE_URL}/api/public/colleges/preview?${params.toString()}`, {
      headers: { 'Content-Type': 'application/json' }
    });
    if (!response.ok) throw new Error(`Public college preview API Error: ${response.status}`);
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      throw new Error('Public college preview endpoint is unavailable. Please restart the backend server.');
    }

    const data = await response.json();
    const result = {
      data: data.data || [],
      total: data.total || 0,
      limit: data.limit || safeLimit
    };
    queryCache.set(cacheKey, result, COLLEGE_PREVIEW_TTL);
    return result;
  }

  static async updateCollege(id, collegeData) {
    try {
      const response = await fetch(`${this.API_BASE_URL}/api/colleges/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(await this.getAuthHeaders())
        },
        body: JSON.stringify(collegeData)
      });
      
      if (response.ok) {
        const data = await response.json();
        return data;
      }

      throw new Error(`Update failed: ${response.status}`);
    } catch (error) {
      console.error('Failed to update college:', error);
      throw error;
    }
  }

  static async getCollegeBySlug(slug) {
    const cacheKey = `college:slug:${slug}`;
    const cached = queryCache.get(cacheKey);
    if (cached) return cached;
    try {
      const response = await fetch(`${this.API_BASE_URL}/api/colleges/slug/${slug}`, {
        headers: { 'Content-Type': 'application/json', ...(await this.getAuthHeaders()) }
      });
      if (response.ok) {
        const data = await response.json();
        queryCache.set(cacheKey, data, COLLEGE_DETAIL_TTL);
        return data;
      }
      return null;
    } catch (error) {
      console.warn('Failed to load college by slug:', error);
      return null;
    }
  }

  static async getAllColleges() {
    return this.getColleges();
  }
}

export default CollegeService;

